import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Modal, Table } from 'react-bootstrap';
import pharmacyService from '../../../services/pharmacyService';
import { usePharmacy } from '../pharmacyContext';
import { PHARMACY_COPY, stockStatusLabel } from '../pharmacyCopy';
import Icon from '../../common/Icons';
import { LoadingBlock, formatDate, formatNumber } from '../ui';

const copy = PHARMACY_COPY.STOCK_TABLE;

function expiryTone(expiry) {
  if (!expiry) return '';
  const days = Math.ceil((new Date(expiry) - new Date()) / 86400000);
  if (days < 0) return 'cp-lot-pill--danger';
  if (days <= 60) return 'cp-lot-pill--warn';
  return 'cp-lot-pill--ok';
}

function expiryLabel(expiry) {
  if (!expiry) return '—';
  const days = Math.ceil((new Date(expiry) - new Date()) / 86400000);
  if (days < 0) return copy.EXPIRY_EXPIRED;
  if (days <= 60) return copy.EXPIRY_SOON;
  return copy.EXPIRY_OK;
}

function stockPillClass(status) {
  if (status === 'OUT_OF_STOCK') return 'cp-status-pill--danger';
  if (status === 'LOW_STOCK') return 'cp-status-pill--warn';
  return 'cp-status-pill--ok';
}

export default function StockTable({
  siteId,
  allowAdjust = false,
  siteLabel = '',
  siteInventoryOnly = false,
  stockLocation = null
}) {
  const { toast } = usePharmacy();
  const isDuty = String(stockLocation || '').toUpperCase() === 'DUTY';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [stockFilter, setStockFilter] = useState('all');
  const [expanded, setExpanded] = useState(null);
  const [lots, setLots] = useState({});
  const [lotsLoading, setLotsLoading] = useState(null);
  const [adjustLot, setAdjustLot] = useState(null);
  const [adjustLotNumber, setAdjustLotNumber] = useState('');
  const [adjustExpiry, setAdjustExpiry] = useState('');
  const [adjustQty, setAdjustQty] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setExpanded(null);
    setLots({});
    setSearchInput('');
    setSearch('');
    setStockFilter('all');
  }, [siteId, stockLocation]);

  useEffect(() => {
    if (isDuty && stockFilter === 'low') setStockFilter('all');
  }, [isDuty, stockFilter]);

  const load = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const res = await pharmacyService.getSiteStock(siteId, {
        search: search || undefined,
        in_stock_only: stockFilter === 'in' ? 'true' : undefined,
        low_stock: stockFilter === 'low' ? 'true' : undefined,
        site_inventory_only: siteInventoryOnly ? 'true' : undefined,
        stock_location: stockLocation || undefined
      });
      let data = res.success && Array.isArray(res.data) ? res.data : [];
      if (stockFilter === 'out') {
        data = data.filter((r) => r.stock_status === 'OUT_OF_STOCK' || Number(r.on_hand) <= 0);
      }
      setRows(data);
    } finally {
      setLoading(false);
    }
  }, [siteId, search, stockFilter, siteInventoryOnly, stockLocation]);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => {
    const units = rows.reduce((acc, r) => acc + Number(r.on_hand || 0), 0);
    const low = rows.filter((r) => r.stock_status === 'LOW_STOCK').length;
    const out = rows.filter((r) => r.stock_status === 'OUT_OF_STOCK').length;
    const expiring = rows.filter((r) => Number(r.expiring_qty || 0) > 0).length;
    return { skus: rows.length, units, low, out, expiring };
  }, [rows]);

  const toggleLots = async (medicationId) => {
    if (expanded === medicationId) {
      setExpanded(null);
      return;
    }
    setExpanded(medicationId);
    if (lots[medicationId]) return;
    setLotsLoading(medicationId);
    try {
      const res = await pharmacyService.getMedicationLots(medicationId, {
        site_id: siteId,
        stock_location: stockLocation || undefined
      });
      setLots((prev) => ({ ...prev, [medicationId]: res.success ? res.data || [] : [] }));
    } finally {
      setLotsLoading(null);
    }
  };

  const openAdjust = (lot) => {
    setAdjustLot(lot);
    setAdjustLotNumber(String(lot.lot_number || ''));
    setAdjustExpiry(lot.expiry_date ? String(lot.expiry_date).slice(0, 10) : '');
    setAdjustQty(String(lot.quantity));
    setAdjustReason('');
  };

  const closeAdjust = () => {
    if (busy) return;
    setAdjustLot(null);
  };

  const submitAdjust = async () => {
    setBusy(true);
    try {
      const res = await pharmacyService.updateLot(adjustLot.id, {
        lot_number: adjustLotNumber.trim(),
        expiry_date: adjustExpiry.trim() || null,
        quantity: parseInt(adjustQty, 10),
        reason: adjustReason.trim()
      });
      if (res.success) {
        toast('success', copy.ADJUST_SUCCESS);
        const medId = adjustLot.medication_id;
        setAdjustLot(null);
        setLots((prev) => {
          const next = { ...prev };
          delete next[medId];
          return next;
        });
        setExpanded(medId);
        setLotsLoading(medId);
        const lotsRes = await pharmacyService.getMedicationLots(medId, {
          site_id: siteId,
          stock_location: stockLocation || undefined
        });
        setLots((prev) => ({ ...prev, [medId]: lotsRes.success ? lotsRes.data || [] : [] }));
        setLotsLoading(null);
        await load();
      } else {
        toast('danger', res.message || copy.ADJUST_FAIL);
      }
    } catch (e) {
      toast('danger', e.message || copy.ADJUST_FAIL);
    } finally {
      setBusy(false);
    }
  };

  const canSaveAdjust =
    !busy &&
    adjustReason.trim().length >= 3 &&
    adjustLotNumber.trim() &&
    adjustQty !== '' &&
    Number.isFinite(Number(adjustQty)) &&
    Number(adjustQty) >= 0;

  return (
    <div className="cp-stock">
      {(siteLabel || allowAdjust) && (
        <div className="cp-stock__banner">
          <div className="cp-stock__banner-main">
            {siteLabel ? (
              <>
                <span className="cp-stock__banner-icon" aria-hidden>
                  <Icon name="warehouse" size={16} />
                </span>
                <span className="cp-stock__banner-title">{siteLabel}</span>
              </>
            ) : null}
          </div>
          <p className="cp-stock__banner-hint mb-0">
            {isDuty
              ? PHARMACY_COPY.STOCK_SITES.DUTY_HINT
              : siteInventoryOnly
                ? PHARMACY_COPY.STOCK_SITES.DISTRIBUTED_HINT
                : allowAdjust
                  ? PHARMACY_COPY.STOCK_SITES.ADJUST_HINT
                  : PHARMACY_COPY.STOCK_SITES.READONLY_HINT}
          </p>
        </div>
      )}

      <section className="cp-catalog-stats cp-stock-stats" aria-label="Synthèse stock">
        <article className="cp-catalog-stat">
          <span className="cp-catalog-stat__label">{copy.STAT_SKUS}</span>
          <span className="cp-catalog-stat__value">{loading ? '—' : formatNumber(stats.skus)}</span>
        </article>
        <article className="cp-catalog-stat cp-catalog-stat--ok">
          <span className="cp-catalog-stat__label">{copy.STAT_UNITS}</span>
          <span className="cp-catalog-stat__value">{loading ? '—' : formatNumber(stats.units)}</span>
        </article>
        {!isDuty ? (
          <article className="cp-catalog-stat cp-catalog-stat--amber">
            <span className="cp-catalog-stat__label">{copy.STAT_LOW}</span>
            <span className="cp-catalog-stat__value">{loading ? '—' : formatNumber(stats.low)}</span>
          </article>
        ) : null}
        <article className="cp-catalog-stat cp-catalog-stat--danger">
          <span className="cp-catalog-stat__label">{copy.STAT_OUT}</span>
          <span className="cp-catalog-stat__value">{loading ? '—' : formatNumber(stats.out)}</span>
        </article>
        <article className="cp-catalog-stat">
          <span className="cp-catalog-stat__label">{copy.STAT_EXPIRING}</span>
          <span className="cp-catalog-stat__value">{loading ? '—' : formatNumber(stats.expiring)}</span>
        </article>
      </section>

      <div className="cp-card cp-stock-panel">
        <div className="cp-catalog-toolbar">
          <div className="cp-search-wrap">
            <Icon name="search" size={16} className="cp-search-wrap__icon" aria-hidden />
            <input
              type="search"
              className="cp-search-wrap__input"
              placeholder={copy.SEARCH_PLACEHOLDER}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label={copy.SEARCH_ARIA}
            />
          </div>
          <div className="cp-catalog-toolbar__end">
            <div className="cp-filter-pills" role="group" aria-label="Filtre stock">
              {(isDuty
                ? [
                    { value: 'all', label: copy.FILTER_ALL },
                    { value: 'in', label: copy.FILTER_IN_STOCK },
                    { value: 'out', label: copy.FILTER_OUT }
                  ]
                : [
                    { value: 'all', label: copy.FILTER_ALL },
                    { value: 'in', label: copy.FILTER_IN_STOCK },
                    { value: 'low', label: copy.FILTER_LOW },
                    { value: 'out', label: copy.FILTER_OUT }
                  ]
              ).map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`cp-filter-pill${stockFilter === opt.value ? ' is-active' : ''}`}
                  onClick={() => setStockFilter(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="cp-icon-btn"
              onClick={load}
              disabled={loading}
              aria-label={copy.REFRESH_ARIA}
              title={copy.REFRESH_ARIA}
            >
              <Icon name="refresh" size={17} aria-hidden />
            </button>
            <div className="cp-catalog-meta">
              <strong>{loading ? '—' : formatNumber(stats.skus)}</strong>
              <span>{copy.RESULTS_LABEL}</span>
            </div>
          </div>
        </div>

        <div className="cp-stock-fefo">
          <Icon name="history" size={14} aria-hidden />
          <span>{copy.FEFO_HINT}</span>
        </div>

        <div className="cp-card__body cp-card__body--flush">
          {loading ? (
            <LoadingBlock label="Chargement du stock…" />
          ) : rows.length === 0 ? (
            <div className="cp-empty-state">
              <span className="cp-empty-state__icon" aria-hidden>
                <Icon name="warehouse" size={28} />
              </span>
              <h3 className="cp-empty-state__title">{copy.EMPTY_TITLE}</h3>
              <p className="cp-empty-state__text">
                {isDuty
                  ? PHARMACY_COPY.STOCK_SITES.DUTY_EMPTY_HINT
                  : siteInventoryOnly
                    ? copy.EMPTY_HINT_SITE
                    : copy.EMPTY_HINT}
              </p>
            </div>
          ) : (
            <div className="cp-table-wrap">
              <Table responsive className="cp-table cp-stock-table mb-0">
                <thead>
                  <tr>
                    <th>{copy.COL_PRODUCT}</th>
                    <th className="text-end">{copy.COL_ON_HAND}</th>
                    {!isDuty ? <th className="text-end">{copy.COL_MIN}</th> : null}
                    <th className="text-end">{copy.COL_LOTS}</th>
                    <th>{copy.COL_EXPIRY}</th>
                    <th>{copy.COL_STATUS}</th>
                    <th className="text-end">{copy.COL_ACTIONS}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const isOpen = expanded === r.medication_id;
                    return (
                      <React.Fragment key={r.medication_id}>
                        <tr className={isOpen ? 'is-expanded' : undefined}>
                          <td>
                            <div className="cp-med-cell">
                              <span className="cp-med-cell__icon" aria-hidden>
                                <Icon name="pills" size={16} />
                              </span>
                              <span className="cp-med-cell__body">
                                <span className="cp-med-cell__name">{r.medication_name}</span>
                                <span className="cp-med-cell__desc">{r.unit_name || '—'}</span>
                              </span>
                            </div>
                          </td>
                          <td className="text-end cp-num">{formatNumber(r.on_hand)}</td>
                          {!isDuty ? (
                            <td className="text-end cp-num cp-num--muted">{formatNumber(r.min_stock)}</td>
                          ) : null}
                          <td className="text-end cp-num">{formatNumber(r.lots_count)}</td>
                          <td>
                            {r.nearest_expiry ? (
                              <span className={`cp-expiry-chip ${expiryTone(r.nearest_expiry)}`}>
                                <span className="cp-expiry-chip__date">{formatDate(r.nearest_expiry)}</span>
                                <span className="cp-expiry-chip__tag">{expiryLabel(r.nearest_expiry)}</span>
                              </span>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td>
                            <span className={`cp-status-pill ${stockPillClass(r.stock_status)}`}>
                              {stockStatusLabel(r.stock_status)}
                            </span>
                          </td>
                          <td className="text-end">
                            <button
                              type="button"
                              className={`cp-btn cp-btn--ghost cp-btn--sm${isOpen ? ' is-active' : ''}`}
                              onClick={() => toggleLots(r.medication_id)}
                              aria-expanded={isOpen}
                            >
                              {isOpen ? copy.HIDE_LOTS : copy.SHOW_LOTS}
                            </button>
                          </td>
                        </tr>
                        {isOpen ? (
                          <tr className="cp-stock-lots-row">
                            <td colSpan={isDuty ? 6 : 7}>
                              <div className="cp-stock-lots">
                                {lotsLoading === r.medication_id || !lots[r.medication_id] ? (
                                  <LoadingBlock label={copy.LOTS_LOADING} />
                                ) : lots[r.medication_id].length === 0 ? (
                                  <p className="cp-stock-lots__empty mb-0">{copy.LOTS_EMPTY}</p>
                                ) : (
                                  <Table size="sm" className="cp-table cp-lots-table mb-0">
                                    <thead>
                                      <tr>
                                        <th>{copy.LOT_NUMBER}</th>
                                        <th>{copy.LOT_EXPIRY}</th>
                                        <th className="text-end">{copy.LOT_QTY}</th>
                                        <th>{copy.LOT_SOURCE}</th>
                                        {allowAdjust ? <th className="text-end"> </th> : null}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {lots[r.medication_id].map((lot) => (
                                        <tr key={lot.id}>
                                          <td>
                                            <span className={`cp-lot-pill ${expiryTone(lot.expiry_date)}`}>
                                              {lot.lot_number}
                                            </span>
                                          </td>
                                          <td>{formatDate(lot.expiry_date)}</td>
                                          <td className="text-end cp-num">{formatNumber(lot.quantity)}</td>
                                          <td>
                                            <span className="cp-muted-chip">{lot.source_type || '—'}</span>
                                          </td>
                                          {allowAdjust ? (
                                            <td className="text-end">
                                              <button
                                                type="button"
                                                className="cp-btn cp-btn--warn cp-btn--sm"
                                                onClick={() => openAdjust(lot)}
                                              >
                                                <Icon name="edit" size={13} aria-hidden />
                                                {copy.LOT_ADJUST}
                                              </button>
                                            </td>
                                          ) : null}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </Table>
                                )}
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </div>

      <Modal
        show={!!adjustLot}
        onHide={closeAdjust}
        centered
        className="cp-modal"
        backdrop={busy ? 'static' : true}
        keyboard={!busy}
      >
        <Modal.Header closeButton={!busy} className="cp-modal__header">
          <div className="cp-modal__intro">
            <span className="cp-modal__badge" aria-hidden>
              <Icon name="edit" size={16} />
            </span>
            <div>
              <Modal.Title as="h2" className="cp-modal__title">
                {copy.ADJUST_TITLE} {adjustLot?.lot_number}
              </Modal.Title>
              <p className="cp-modal__desc">{copy.ADJUST_DESC}</p>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="cp-modal__body">
          {adjustLot ? (
            <div className="cp-adjust-summary">
              <div>
                <span className="cp-adjust-summary__label">{copy.CURRENT_QTY}</span>
                <strong className="cp-adjust-summary__value">{formatNumber(adjustLot.quantity)}</strong>
              </div>
              <div>
                <span className="cp-adjust-summary__label">{copy.LOT_NUMBER}</span>
                <strong className="cp-adjust-summary__value">{adjustLot.lot_number || '—'}</strong>
              </div>
              <div>
                <span className="cp-adjust-summary__label">{copy.LOT_EXPIRY}</span>
                <strong className="cp-adjust-summary__value">{formatDate(adjustLot.expiry_date)}</strong>
              </div>
            </div>
          ) : null}
          <section className="cp-form-section">
            <div className="cp-form-grid">
              <Form.Group className="cp-form-field">
                <Form.Label>{copy.ADJUST_LOT_NUMBER} *</Form.Label>
                <Form.Control
                  type="text"
                  autoFocus
                  value={adjustLotNumber}
                  onChange={(e) => setAdjustLotNumber(e.target.value)}
                  disabled={busy}
                />
                <Form.Text>{copy.ADJUST_LOT_NUMBER_HINT}</Form.Text>
              </Form.Group>
              <Form.Group className="cp-form-field">
                <Form.Label>{copy.ADJUST_EXPIRY}</Form.Label>
                <Form.Control
                  type="date"
                  value={adjustExpiry}
                  onChange={(e) => setAdjustExpiry(e.target.value)}
                  disabled={busy}
                />
                <Form.Text>{copy.ADJUST_EXPIRY_HINT}</Form.Text>
              </Form.Group>
              <Form.Group className="cp-form-field cp-form-field--full">
                <Form.Label>{copy.ADJUST_QTY} *</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  value={adjustQty}
                  onChange={(e) => setAdjustQty(e.target.value)}
                  disabled={busy}
                />
                <Form.Text>{copy.ADJUST_QTY_HINT}</Form.Text>
              </Form.Group>
              <Form.Group className="cp-form-field cp-form-field--full">
                <Form.Label>{copy.ADJUST_REASON} *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={adjustReason}
                  onChange={(e) => setAdjustReason(e.target.value)}
                  placeholder={copy.ADJUST_REASON_PLACEHOLDER}
                  disabled={busy}
                />
                <Form.Text>{copy.ADJUST_REASON_HINT}</Form.Text>
              </Form.Group>
            </div>
          </section>
        </Modal.Body>
        <Modal.Footer className="cp-modal__footer">
          <button type="button" className="cp-btn cp-btn--ghost" onClick={closeAdjust} disabled={busy}>
            {copy.ADJUST_CANCEL}
          </button>
          <button type="button" className="cp-btn cp-btn--primary" onClick={submitAdjust} disabled={!canSaveAdjust}>
            {busy ? copy.ADJUST_SAVING : copy.ADJUST_SAVE}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
